import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PreviewImage } from '../../../global-types/images';
import {
  PREVIEW_GENERATION_ERROR_DEDUPE_KEY,
  reportPreviewGenerationErrorOnce,
} from '../utils/report-preview-generation-error';

export const PREVIEW_TIMEOUT_MS = 10000;

export type PreviewGenerationStatus = 'idle' | 'loading' | 'error' | 'success';

/** Thrown when generation exceeds the timeout so the error path can distinguish it. */
export class PreviewTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Preview generation timed out after ${timeoutMs}ms`);
    this.name = 'PreviewTimeoutError';
  }
}

type UsePreviewGenerationParams = {
  generate: () => Promise<PreviewImage[]>;
  onSuccess: (images: PreviewImage[]) => void;
  onError?: (error: Error) => void;
  timeoutMs?: number;
};

export const usePreviewGeneration = ({
  generate,
  onSuccess,
  onError,
  timeoutMs = PREVIEW_TIMEOUT_MS,
}: UsePreviewGenerationParams) => {
  const [status, setStatus] = useState<PreviewGenerationStatus>('idle');
  const [error, setError] = useState<Error | null>(null);

  // Refs keep run/retry stable so effects that call run() don't loop, and let
  // us ignore a stale attempt that resolves after a timeout or retry.
  const generateRef = useRef(generate);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  // Update in a layout effect rather than during render to stay safe under
  // concurrent/interrupted renders.
  useLayoutEffect(() => {
    generateRef.current = generate;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  });
  const attemptRef = useRef(0);
  // Ignore results that resolve after the consumer unmounts.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const run = useCallback(async () => {
    const attemptId = attemptRef.current + 1;
    attemptRef.current = attemptId;
    setStatus('loading');
    setError(null);

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new PreviewTimeoutError(timeoutMs)), timeoutMs);
    });

    try {
      const images = await Promise.race([generateRef.current(), timeoutPromise]);
      if (attemptId !== attemptRef.current || !isMountedRef.current) return;
      setStatus('success');
      onSuccessRef.current(images);
    } catch (err) {
      if (attemptId !== attemptRef.current || !isMountedRef.current) return;
      const normalizedError = err instanceof Error ? err : new Error(String(err));
      setError(normalizedError);
      setStatus('error');
      onErrorRef.current?.(normalizedError);
      reportPreviewGenerationErrorOnce(PREVIEW_GENERATION_ERROR_DEDUPE_KEY, normalizedError);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }, [timeoutMs]);

  const retry = useCallback(() => {
    void run();
  }, [run]);

  return { status, error, run, retry };
};
